import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as myGlobals from '../globals';
import { DateTime } from 'luxon';
import { AppInfoService } from './app-info.service';
import { LoggerService } from './logger.service';
import { Select } from '@ngxs/store'
import { UserState, UserStateModel } from 'src/app/state-man/state/user.state';
import { Observable } from 'rxjs';
import { User } from 'src/app/state-man/models/user.model';
import { UiService } from './ui.service';
import { WhatsNewService } from './whats-new.service';

/** Compares the app's version with the server's app_settings 'version' row: on a timer while signed in, and on demand. */
@Injectable({
  providedIn: 'root'
})
export class ServerVersionService {
  @Select(UserState.getUser) user$: Observable<User>
  /** When the server was last asked, for the About page. */
  readonly lastChecked$ = new BehaviorSubject<DateTime | null>(null)
  user: User;

  constructor (
      private appInfoService: AppInfoService,
      private uiService: UiService,
      private whatsNew: WhatsNewService
  ) {
    this.user = UserStateModel.getDefaultUser()
  }

  startWatching() {
    this.user$.subscribe((val: User) => {   
      this.user = val      
    })
    this.setVersionCheckTimer();
  }

  setVersionCheckTimer() {
    setTimeout(() => {
      this.checkAppVersion();
    }, myGlobals.welcome_check_version_timeout_ms);
  }

  async checkAppVersion() {
    // Need to be logged in to check versions, if not, reset timer and return.    
    if (!this.user.is_logged_in) {
      this.setVersionCheckTimer()
      return;
    }

    try {
      await this.checkNow()
    } catch (error) {      
      LoggerService.log(error)   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
    }  
    this.setVersionCheckTimer()
  }

  /** 'update' when the server names a different version than this build. Throws when the server cannot be reached. */
  async checkNow(): Promise<'latest' | 'update'> {
    const serverVersion = await this.getServerVersion()
    // Same pass, so the What's New notes are as fresh as the version; a failure here is not a version-check failure.
    await this.whatsNew.refresh().catch(e => LoggerService.log(e))
    return serverVersion !== 'UNKNOWN' && serverVersion !== this.getLocalAppVersion() ? 'update' : 'latest'
  }

  getLocalAppVersion() {
    return myGlobals.app_version_number;
  }

  async getServerVersion() {
    let serverVersion = 'UNKNOWN';
    const api_results = await this.appInfoService.getAll();
    if (api_results) {
      const element = api_results.find(function(element: any) {
        return element.key === 'version';
      });
      if (element) {
        serverVersion = element.value;
      }
      this.lastChecked$.next(DateTime.now());
    }
    return serverVersion;
  }
}
